import React from 'react';

import { locale } from '@trqk/utils';
import { IconWarningSign } from '@trqk/components';

import {
    ChoosePlan,
    CurrentFreeTrialPlan,
    CurrentPaidPlan,
    PublisherEditionPlan,
} from '.';
import { planWidth } from '../constants';

interface PaymentPlanContentsProps {
    chargebeeInstance: null | ChargebeeInstance;
    chargebeePlanSettings: ChargebeePlanSettings[] | null;
    onButtonViewed(): void;
    onSubscriptionChanged(): void;
    openCheckoutOptions: ChargebeeOpenCheckoutOptionsPartial;
    qrrOverageMessage: JSX.Element | null;
    subscriptionSettings: Maybe<ChargebeeSubscriptionSettings>;
    redirectUrl: string;
    usage: PaymentPlanUsage;
}

export const PaymentPlanContents = ({
    chargebeeInstance,
    chargebeePlanSettings,
    onButtonViewed,
    onSubscriptionChanged,
    openCheckoutOptions,
    qrrOverageMessage,
    subscriptionSettings,
    redirectUrl,
    usage,
}: PaymentPlanContentsProps): JSX.Element => {
    const isFreeTrial =
        subscriptionSettings?.subscription_plan_type === 'CHARGEBEE_PLAN_TRIAL';
    const isPublisherEdition =
        subscriptionSettings?.subscription_plan_type ===
        'CHARGEBEE_PLAN_PUBLISHER';
    const planSettings =
        chargebeePlanSettings === null
            ? null
            : chargebeePlanSettings.find(
                  ({ id }: ChargebeePlanSettings): boolean =>
                      id === subscriptionSettings?.subscription_plan_type,
              ) ?? null;

    const showWarning = qrrOverageMessage !== null || usage.exceedsCapacity;

    return (
        <div className="px-12 py-10 bg-white rounded-lg">
            <div className="flex justify-between'">
                <h4 data-cy="payment-plan-section-title">
                    {locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.TITLE}
                </h4>
            </div>
            {!showWarning && (
                <div className="my-4 flex items-center font-normal">
                    {locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.FEES_BASED_ON}
                </div>
            )}
            {showWarning && (
                <div className="my-4 border rounded-lg border-tertiary-200 p-4">
                    {!usage.exceedsCapacity && qrrOverageMessage && (
                        <>{qrrOverageMessage}</>
                    )}
                    {usage.exceedsCapacity && (
                        <div
                            className="grid"
                            style={{ gridTemplateColumns: '30px auto' }}
                        >
                            <IconWarningSign
                                fillColor="quinary-500"
                                width={20}
                            />
                            <div className="text-sm">
                                <p>
                                    {
                                        locale.enUS.ACCOUNT.SECTIONS
                                            .PAYMENT_PLAN.EXCESS_USAGE
                                            .OUTGROWN_PLAN
                                    }
                                </p>
                                <p className="mt-2 text-primary-500 font-semibold">
                                    {
                                        locale.enUS.ACCOUNT.SECTIONS
                                            .PAYMENT_PLAN.EXCESS_USAGE
                                            .SELECT_RECOMMENDED_PLAN
                                    }
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className="mt-6 flex w-full justify-around">
                {isFreeTrial && planSettings !== null && (
                    <>
                        <CurrentFreeTrialPlan
                            planSettings={planSettings}
                            subscriptionSettings={subscriptionSettings}
                            width={planWidth}
                            usage={usage}
                        />
                        <ChoosePlan
                            chargebeeInstance={chargebeeInstance}
                            chargebeePlanSettings={chargebeePlanSettings}
                            onButtonViewed={onButtonViewed}
                            openCheckoutOptions={openCheckoutOptions}
                            redirectUrl={redirectUrl}
                            subscriptionSettings={subscriptionSettings}
                            width={planWidth}
                            usage={usage}
                        />
                    </>
                )}
                {!isFreeTrial && (
                    <CurrentPaidPlan
                        chargebeeInstance={chargebeeInstance}
                        isPublisherEdition={isPublisherEdition}
                        onButtonViewed={onButtonViewed}
                        onSubscriptionChanged={onSubscriptionChanged}
                        planSettings={planSettings}
                        subscriptionSettings={subscriptionSettings}
                        width={planWidth}
                        usage={usage}
                    />
                )}
                {!isPublisherEdition &&
                    !isFreeTrial &&
                    usage.exceedsCapacity && (
                        <ChoosePlan
                            chargebeeInstance={chargebeeInstance}
                            chargebeePlanSettings={chargebeePlanSettings}
                            onButtonViewed={onButtonViewed}
                            openCheckoutOptions={openCheckoutOptions}
                            redirectUrl={redirectUrl}
                            subscriptionSettings={subscriptionSettings}
                            width={planWidth}
                            usage={usage}
                        />
                    )}
                {!isPublisherEdition && (
                    <PublisherEditionPlan width={planWidth} />
                )}
            </div>
        </div>
    );
};
